import type { Middleware } from '@reduxjs/toolkit';

import { AppConfigService } from 'features/app-config/services';
import type { AppDispatch } from 'features/app/store/store';
import { markCallAsAbandoned } from 'features/call/call-base/store/callSlice';
import { isHearingToDeafSelector } from 'features/call/call-base/store/selectors';
import {
  isDeafDisconnectedSelector,
  selectDeafHasConnected,
} from 'features/call/call-deaf/store';
import {
  isPrimaryHearingDisconnectedSelector,
  primaryHearingStatusSelector,
} from 'features/call/call-hearing/store';
import { sendAnalyticsInfo } from 'features/analytics/helpers';
import { LoggerService } from 'features/logger/services';
import { isVrsModeSelector } from 'features/multi-mode/store';

/**
 * Timer state tracked per store instance to prevent race conditions
 * and memory leaks across multiple store instances (e.g., in tests)
 */
interface TimerState {
  timer: NodeJS.Timeout | null;
  isActive: boolean;
  timerStartedForCurrentCall: boolean;
}

const timerStateMap = new WeakMap<any, TimerState>();

/**
 * Middleware that monitors H2D calls and marks them as abandoned if:
 * 1. Both parties are disconnected within the configured timer window, AND
 * 2. Deaf party was never connected
 *
 * The timer starts once when the hearing party first connects and cannot restart.
 * After the timer expires, no abandonment checking occurs for the remainder of the call.
 */
export const abandonedHearingCallTimerMiddleware: Middleware =
  (store) => (next) => (action: unknown) => {
    // Skip timer when Relay Config is set to zero seconds.
    if (isAbandonmentTimerDisabled()) {
      return next(action);
    }
    try {
      if (!isRelevantAction(action)) {
        return next(action);
      }

      const result = next(action);

      const state = store.getState();
      const timerState = getTimerState(store);

      const actionType = (action as { type: string }).type;
      const primaryHearingStatus = primaryHearingStatusSelector(state);

      // Reset timer state completely on call reset
      if (actionType === 'call/performResets/pending') {
        resetTimer(store);
        return result;
      }

      if (!shouldMonitorCall(state)) {
        return result;
      }

      if (shouldStartTimer(primaryHearingStatus, timerState)) {
        timerState.isActive = true;
        timerState.timerStartedForCurrentCall = true;
        const timerMs =
          AppConfigService.abandonedHearingCallTimerSeconds * 1000;
        console.log('Timer started for abandoned hearing call, ms:', timerMs);
        timerState.timer = setTimeout(() => {
          console.log('Timer ended for abandoned hearing call');
          clearTimer(store);
        }, timerMs);
      }

      const isPrimaryHearingDisconnected =
        isPrimaryHearingDisconnectedSelector(state);
      const isDeafDisconnected = isDeafDisconnectedSelector(state);

      if (
        shouldCheckAbandonment(
          timerState,
          isPrimaryHearingDisconnected,
          isDeafDisconnected
        )
      ) {
        const hasConnected = selectDeafHasConnected(state);
        if (!hasConnected) {
          handleAbandonment(store);
        }
      }

      return result;
    } catch (error) {
      LoggerService.error(error);
      console.error('[abandonedHearingCallTimerMiddleware] Error:', error);
      return next(action);
    }
  };

/**
 * Get or initialize timer state for a store instance
 */
const getTimerState = (store: object): TimerState => {
  if (!timerStateMap.has(store)) {
    timerStateMap.set(store, {
      timer: null,
      isActive: false,
      timerStartedForCurrentCall: false,
    });
  }
  return timerStateMap.get(store)!;
};
/**
 * Clear and reset timer state
 */
const clearTimer = (store: object): void => {
  const state = getTimerState(store);
  if (state.timer) {
    clearTimeout(state.timer);
    state.timer = null;
  }
  state.isActive = false;
};

/**
 * Reset all timer state including hasTimerStartedThisCall (for call resets)
 */
const resetTimer = (store: object): void => {
  const state = getTimerState(store);
  if (state.timer) {
    clearTimeout(state.timer);
    state.timer = null;
  }
  state.isActive = false;
  state.timerStartedForCurrentCall = false;
};

// Helper: Check if action is relevant for timer logic
const isRelevantAction = (action: unknown): boolean => {
  const relevantActions = [
    'call-hearing/setHearingStatus', // Start timer when hearing connects; check abandonment when hearing disconnects
    'call-hearing/setHearingStatusWithId', // Alternative action for hearing status changes
    'call-deaf/setDeafStatus', // Check abandonment when deaf disconnects
    'call/performResets/pending', // Reset timer state when call is reset
  ];
  return (
    typeof action === 'object' &&
    action !== null &&
    relevantActions.includes((action as { type: string }).type)
  );
};

// Helper: Check if abandonment timer is disabled
const isAbandonmentTimerDisabled = (): boolean => {
  return AppConfigService.abandonedHearingCallTimerSeconds === 0;
};

// Helper: Should monitor this call (H2D and VRS)
const shouldMonitorCall = (state: any): boolean => {
  return isHearingToDeafSelector(state) && isVrsModeSelector(state);
};

// Helper: Should start timer for this call
const shouldStartTimer = (
  primaryHearingStatus: string,
  timerState: TimerState
): boolean => {
  return (
    primaryHearingStatus === 'connected' &&
    !timerState.timerStartedForCurrentCall
  );
};

// Helper: Should check for abandonment
const shouldCheckAbandonment = (
  timerState: TimerState,
  isPrimaryHearingDisconnected: boolean,
  isDeafDisconnected: boolean
): boolean => {
  return (
    timerState.isActive && isPrimaryHearingDisconnected && isDeafDisconnected
  );
};

// Helper: Handle call abandonment
const handleAbandonment = (store: any) => {
  clearTimer(store);
  (store.dispatch as AppDispatch)(markCallAsAbandoned());
  (store.dispatch as AppDispatch)(
    sendAnalyticsInfo({
      Method: 'markCallAsAbandoned',
      Message:
        'Call marked as abandoned (both parties disconnected during timer window, deaf was never connected)',
      Event: 'CallAbandoned',
    })
  );
};
